import {
  ChevronUp,
  Edit,
  Eye,
  RotateCcw,
  Trash2,
} from "feather-icons-react/build/IconComponents";
import { React, useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import ImageWithBasePath from "../../core/img/imagewithbasebath";
import Brand from "../../core/modals/inventory/brand";
import { all_routes } from "../../Router/all_routes";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import Table from "../../core/pagination/datatable";
import { setToogleHeader } from "../../core/redux/action";
import { Download } from "react-feather";
import { fetchProductList, deleteProduct } from "../../api/productApi"; // Adjust the import path as necessary
import {
  getCurrentUserPerms,
  hasRead,
  hasWrite,
  isCurrentUserAdmin,
} from "../../utils/perm";

const DEFAULT_PRODUCT_IMAGE = "/assets/img/by-default.png";

const ProductList = () => {
  // const dataSource = useSelector((state) => state.rootReducer.product_list);
  const dispatch = useDispatch();
  const data = useSelector((state) => state.rootReducer.toggle_header);
  const user = useSelector((state) => state?.auth?.user);
  const userPerms = getCurrentUserPerms(user?.permissions || []);
  const canWriteProducts =
    isCurrentUserAdmin(user) ||
    userPerms.includes("*") ||
    hasWrite(userPerms, "products");
  const canExportProducts =
    isCurrentUserAdmin(user) ||
    userPerms.includes("*") ||
    hasRead(userPerms, "catalogUploads");

  const [productData, setProductData] = useState([]);
  const [selectedProductId, setSelectedProductId] = useState(null);
  const [loadingProducts, setLoadingProducts] = useState(true);

  useEffect(() => {
    let isMounted = true;

    const loadProducts = async () => {
      try {
        if (isMounted) setLoadingProducts(true); // 👈 start loading

        const response = await fetchProductList();

        if (isMounted) {
          const formatted = response.map((item) => ({
            id: item._id || item.id,
            key: item._id || item.id,
            _id: item._id || item.id,
            sku: item.SKU || item.sku,
            product: item.Product_Name || item.productName || "Unnamed",
            img: item.image || item.images?.[0] || DEFAULT_PRODUCT_IMAGE,
            category: item.Category || "-",
            brand: item.Brand || "-",
            price: item.Price || "-",
            unit: item.Unit || "-",
            qty: item.Qty?.toString() || "-",
            createdby: item.Created_By || "Unknown",
            profile_image: item.profile_image || "/uploads/default-user.png",
            manufactureddate: item.manufacturedDate
              ? new Date(item.manufacturedDate).toLocaleDateString()
              : "-",
            expireddate: item.expiryDate
              ? new Date(item.expiryDate).toLocaleDateString()
              : "-",
          }));

          setProductData(formatted);
        }
      } catch (error) {
        console.error("Failed to load products:", error);
        // optional: toast here if you use react-toastify
      } finally {
        if (isMounted) setLoadingProducts(false); // 👈 stop loading
      }
    };

    loadProducts();

    return () => {
      isMounted = false;
    };
  }, []);

  const route = all_routes;

  const columns = [
    {
      title: window.translate("SKU"),
      dataIndex: "sku",
      sorter: (a, b) => a.sku.length - b.sku.length,
    },
    {
      title: window.translate("Product"),
      dataIndex: "product",
      render: (text, record) => (
        <span className="productimgname d-flex align-items-center">
          <img
            src={record.img}
            alt="Product"
            onError={(e) => {
              e.currentTarget.onerror = null;
              e.currentTarget.src = DEFAULT_PRODUCT_IMAGE;
            }}
            style={{
              width: "80px",
              height: "auto",
              borderRadius: "4px",
              marginRight: "10px",
            }}
          />
          <span>{text}</span>
        </span>
      ),
      sorter: (a, b) => (a.product || "").length - (b.product || "").length,
    },

    {
      title: window.translate("Category"),
      dataIndex: "category",
      sorter: (a, b) => a.category.length - b.category.length,
    },

    {
      title: window.translate("Brand"),
      dataIndex: "brand",
      sorter: (a, b) => a.brand.length - b.brand.length,
    },
    {
      title: window.translate("Price"),
      dataIndex: "price",
      sorter: (a, b) => a.price.length - b.price.length,
    },
    {
      title: window.translate("Unit"),
      dataIndex: "unit",
      sorter: (a, b) => a.unit.length - b.unit.length,
    },
    {
      title: window.translate("Qty"),
      dataIndex: "qty",
      sorter: (a, b) => a.qty.length - b.qty.length,
    },

    // {
    //   title: "Created By",
    //   dataIndex: "createdby",
    //   render: (text, record) => (
    //     <span className="userimgname">
    //       <Link to="/profile" className="product-img">
    //         <ImageWithBasePath alt="" src={record.img} />
    //       </Link>
    //       <Link to="/profile">{text}</Link>
    //     </span>
    //   ),
    //   sorter: (a, b) => a.createdby.length - b.createdby.length,
    // },
    {
      title: window.translate("Action"),
      dataIndex: "action",
      render: (text, record) => (
        <div className="action-table-data">
          <div className="edit-delete-action">
            <Link
              className="me-2 p-2"
              to={`/product-details/${record.id ?? record._id}`}
            >
              <Eye className="feather-view" />
            </Link>
            {canWriteProducts && (
              <Link
                className="me-2 p-2"
                to={
                  "/edit-product/" + (record.id ?? record._id)
                }
              >
                <Edit className="feather-edit" />
              </Link>
            )}
            {canWriteProducts && (
              <Link
                className="confirm-text p-2"
                to="#"
                data-bs-toggle="modal"
                data-bs-target="#delete-modal"
                onClick={() => setSelectedProductId(record.id)}
              >
                <Trash2 className="feather-trash-2" />
              </Link>
            )}
          </div>
        </div>
      ),
      sorter: (a, b) => a.createdby.length - b.createdby.length,
    },
  ];

  const renderTooltip = (props) => (
    <Tooltip id="pdf-tooltip" {...props}>
      Pdf
    </Tooltip>
  );
  const renderExcelTooltip = (props) => (
    <Tooltip id="excel-tooltip" {...props}>
      Excel
    </Tooltip>
  );

  const renderRefreshTooltip = (props) => (
    <Tooltip id="refresh-tooltip" {...props}>
      Refresh
    </Tooltip>
  );
  const renderCollapseTooltip = (props) => (
    <Tooltip id="refresh-tooltip" {...props}>
      Collapse
    </Tooltip>
  );
  return (
    <>
      <div className="page-wrapper">
        <div className="content">
          <div className="page-header">
            <div className="add-item d-flex">
              <div className="page-title">
                <h4>{window.translate("Product List")}</h4>
                <h6>{window.translate("Manage your products")}</h6>
              </div>
            </div>
            <ul className="table-top-head">
              {canExportProducts && (
                <li>
                  <OverlayTrigger placement="top" overlay={renderTooltip}>
                    <Link>
                      <ImageWithBasePath
                        src="assets/img/icons/pdf.svg"
                        alt="img"
                      />
                    </Link>
                  </OverlayTrigger>
                </li>
              )}
              {canExportProducts && (
                <li>
                  <OverlayTrigger placement="top" overlay={renderExcelTooltip}>
                    <Link data-bs-toggle="tooltip" data-bs-placement="top">
                      <ImageWithBasePath
                        src="assets/img/icons/excel.svg"
                        alt="img"
                      />
                    </Link>
                  </OverlayTrigger>
                </li>
              )}

              <li>
                <OverlayTrigger placement="top" overlay={renderRefreshTooltip}>
                  <Link data-bs-toggle="tooltip" data-bs-placement="top">
                    <RotateCcw />
                  </Link>
                </OverlayTrigger>
              </li>
              <li>
                <OverlayTrigger placement="top" overlay={renderCollapseTooltip}>
                  <Link
                    data-bs-toggle="tooltip"
                    data-bs-placement="top"
                    id="collapse-header"
                    className={data ? "active" : ""}
                    onClick={(e) => {
                      e.preventDefault();
                      dispatch(setToogleHeader(!data));
                    }}
                  >
                    <ChevronUp />
                  </Link>
                </OverlayTrigger>
              </li>
            </ul>
            {canWriteProducts && (
              <div className="page-btn">
                <Link to={route.addproduct} className="btn btn-primary">
                  <i className="ti ti-circle-plus me-2 ms-2"></i>
                  {window.translate("Add New Product")}
                </Link>
              </div>
            )}
            {canExportProducts && (
              <div className="page-btn import">
                <Link
                  to="#"
                  className="btn btn-secondary color"
                  data-bs-toggle="modal"
                  data-bs-target="#view-notes"
                >
                  <Download className="feather me-2" />
                  Import Product
                </Link>
              </div>
            )}
          </div>
          {/* /product list */}
          <div className="card table-list-card">
            <div className="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3">
              <div className="search-set"></div>
              <div className="d-flex table-dropdown my-xl-auto right-content align-items-center flex-wrap row-gap-3">
                <div className="dropdown me-2">
                  <Link
                    to="#"
                    className="dropdown-toggle btn btn-white btn-md d-inline-flex align-items-center d-none"
                    data-bs-toggle="dropdown"
                  >
                    Product
                  </Link>
                  <ul className="dropdown-menu  dropdown-menu-end p-3">
                    <li>
                      <Link to="#" className="dropdown-item rounded-1">
                        Lenovo IdeaPad 3
                      </Link>
                    </li>
                    <li>
                      <Link to="#" className="dropdown-item rounded-1">
                        Beats Pro{" "}
                      </Link>
                    </li>
                    <li>
                      <Link to="#" className="dropdown-item rounded-1">
                        Nike Jordan
                      </Link>
                    </li>
                    <li>
                      <Link to="#" className="dropdown-item rounded-1">
                        Apple Series 5 Watch
                      </Link>
                    </li>
                  </ul>
                </div>
                <div className="dropdown me-2">
                  <Link
                    to="#"
                    className="dropdown-toggle btn btn-white btn-md d-inline-flex align-items-center d-none"
                    data-bs-toggle="dropdown"
                  >
                    Created By
                  </Link>
                  <ul className="dropdown-menu  dropdown-menu-end p-3">
                    <li>
                      <Link to="#" className="dropdown-item rounded-1">
                        James Kirwin
                      </Link>
                    </li>
                    <li>
                      <Link to="#" className="dropdown-item rounded-1">
                        Francis Chang
                      </Link>
                    </li>
                    <li>
                      <Link to="#" className="dropdown-item rounded-1">
                        Antonio Engle
                      </Link>
                    </li>
                    <li>
                      <Link to="#" className="dropdown-item rounded-1">
                        Leo Kelly
                      </Link>
                    </li>
                  </ul>
                </div>
                <div className="dropdown me-2">
                  <Link
                    to="#"
                    className="dropdown-toggle btn btn-white btn-md d-inline-flex align-items-center d-none"
                    data-bs-toggle="dropdown"
                  >
                    Category
                  </Link>
                  <ul className="dropdown-menu  dropdown-menu-end p-3">
                    <li>
                      <Link to="#" className="dropdown-item rounded-1">
                        Computers
                      </Link>
                    </li>
                    <li>
                      <Link to="#" className="dropdown-item rounded-1">
                        Electronics
                      </Link>
                    </li>
                    <li>
                      <Link to="#" className="dropdown-item rounded-1">
                        Shoe
                      </Link>
                    </li>
                    <li>
                      <Link to="#" className="dropdown-item rounded-1">
                        Electronics
                      </Link>
                    </li>
                  </ul>
                </div>
                <div className="dropdown me-2">
                  <Link
                    to="#"
                    className="dropdown-toggle btn btn-white btn-md d-inline-flex align-items-center d-none"
                    data-bs-toggle="dropdown"
                  >
                    Brand
                  </Link>
                  <ul className="dropdown-menu  dropdown-menu-end p-3">
                    <li>
                      <Link to="#" className="dropdown-item rounded-1">
                        Lenovo
                      </Link>
                    </li>
                    <li>
                      <Link to="#" className="dropdown-item rounded-1">
                        Beats
                      </Link>
                    </li>
                    <li>
                      <Link to="#" className="dropdown-item rounded-1">
                        Nike
                      </Link>
                    </li>
                    <li>
                      <Link to="#" className="dropdown-item rounded-1">
                        Apple
                      </Link>
                    </li>
                  </ul>
                </div>
                <div className="dropdown">
                  <Link
                    to="#"
                    className="dropdown-toggle btn btn-white btn-md d-inline-flex align-items-center d-none"
                    data-bs-toggle="dropdown"
                  >
                    {window.translate("sortLast7Days")}
                  </Link>
                  <ul className="dropdown-menu  dropdown-menu-end p-3">
                    <li>
                      <Link to="#" className="dropdown-item rounded-1">
                        {window.translate("recentlyAdded")}
                      </Link>
                    </li>
                    <li>
                      <Link to="#" className="dropdown-item rounded-1">
                        {window.translate("ascending")}
                      </Link>
                    </li>
                    <li>
                      <Link to="#" className="dropdown-item rounded-1">
                        {window.translate("descending")}
                      </Link>
                    </li>
                    <li>
                      <Link to="#" className="dropdown-item rounded-1">
                        {window.translate("lastMonth")}
                      </Link>
                    </li>
                    <li>
                      <Link to="#" className="dropdown-item rounded-1">
                        {window.translate("last7Days")}
                      </Link>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
            <div className="card-body">
              {/* {filter} */}
              <div className="table-responsive">
                {loadingProducts ? (
                  // 🔄 Centered Bootstrap spinner while loading
                  <div className="d-flex justify-content-center align-items-center py-5">
                    <div className="text-center">
                      <div className="spinner-border" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      <p className="mb-0 mt-3 text-muted fw-medium">
                        {window.translate("Loading products...")}
                      </p>
                    </div>
                  </div>
                ) : productData.length > 0 ? (
                  <Table
                    columns={columns}
                    dataSource={productData}
                    rowKey={(record) => record.id || record._id}
                  />
                ) : (
                  // 🎯 Centered "no data" message using Bootstrap only
                  <div className="d-flex justify-content-center align-items-center py-5">
                    <p className="mb-0 text-muted fw-medium text-center">
                      {window.translate(
                        "Loading products or no data available"
                      )}
                      .
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
          {/* /product list */}
          <Brand />
        </div>
      </div>
      <>
        {/* delete modal */}
        <div className="modal fade" id="delete-modal">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="page-wrapper-new p-0">
                <div className="content p-5 px-3 text-center">
                  <span className="rounded-circle d-inline-flex p-2 bg-danger-transparent mb-2">
                    <i className="ti ti-trash fs-24 text-danger" />
                  </span>
                  <h4 className="fs-20 text-gray-9 fw-bold mb-2 mt-1">
                    {window.translate("Delete Product")}
                  </h4>
                  <p className="text-gray-6 mb-0 fs-16">
                    {window.translate(
                      "Are you sure you want to delete product?"
                    )}
                  </p>
                  <div className="modal-footer-btn mt-3 d-flex justify-content-center">
                    <button
                      type="button"
                      className="btn me-2 btn-secondary fs-13 fw-medium p-2 px-3 shadow-none"
                      data-bs-dismiss="modal"
                    >
                      {window.translate("cancel")}
                    </button>
                    {/* <button
                      type="button"
                      className="btn btn-primary fs-13 fw-medium p-2 px-3"
                      data-bs-dismiss="modal"
                    >
                      {window.translate("Yes Delete")}
                    </button> */}

                    <button
                      type="button"
                      className="btn btn-primary fs-13 fw-medium p-2 px-3"
                      data-bs-dismiss="modal"
                      onClick={async () => {
                        await deleteProduct(selectedProductId);
                        setTimeout(() => {
                          window.location.reload(); // Refreshes the whole page
                          // OR
                          // loadProducts(); // Prefer this if you want a smoother UX without full reload
                        }, 800);
                      }}
                    >
                      {window.translate("Yes Delete")}
                    </button>
                    {/* <button
                      type="button"
                      className="btn btn-primary fs-13 fw-medium p-2 px-3"
                      data-bs-dismiss="modal"
                      onClick={() => deleteProduct(productId)}
                    >
                      {window.translate("Yes Delete")}
                    </button> */}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </>
    </>
  );
};

export default ProductList;
{
  /* <div className="table-top">
              <div className="search-set">
                <div className="search-input">
                  <input
                    type="text"
                    placeholder="Search"
                    className="form-control form-control-sm formsearch"
                  />
                  <Link to className="btn btn-searchset">
                    <i data-feather="search" className="feather-search" />
                  </Link>
                </div>
              </div>
              <div className="search-path">
                <Link
                  className={`btn btn-filter ${
                    isFilterVisible ? "setclose" : ""
                  }`}
                  id="filter_search"
                >
                  <Filter
                    className="filter-icon"
                    onClick={toggleFilterVisibility}
                  />
                  <span onClick={toggleFilterVisibility}>
                    <ImageWithBasePath
                      src="assets/img/icons/closes.svg"
                      alt="img"
                    />
                  </span>
                </Link>
              </div>
              <div className="form-sort">
                <Sliders className="info-img" />
                <Select
                  className="img-select"
                  classNamePrefix="react-select"
                  options={options}
                  placeholder="14 09 23"
                />
              </div>
            </div> */
}
{
  /* /Filter */
}
{
  /* <div
              className={`card${isFilterVisible ? " visible" : ""}`}
              id="filter_inputs"
              style={{ display: isFilterVisible ? "block" : "none" }}
            >
              <div className="card-body pb-0">
                <div className="row">
                  <div className="col-lg-12 col-sm-12">
                    <div className="row">
                      <div className="col-lg-2 col-sm-6 col-12">
                        <div className="input-blocks">
                          <Box className="info-img" />
                          <Select
                            className="img-select"
                            classNamePrefix="react-select"
                            options={productlist}
                            placeholder="Choose Product"
                          />
                        </div>
                      </div>
                      <div className="col-lg-2 col-sm-6 col-12">
                        <div className="input-blocks">
                          <StopCircle className="info-img" />
                          <Select
                            className="img-select"
                            classNamePrefix="react-select"
                            options={categorylist}
                            placeholder="Choose Category"
                          />
                        </div>
                      </div>
                      <div className="col-lg-2 col-sm-6 col-12">
                        <div className="input-blocks">
                          <GitMerge className="info-img" />
                          <Select
                            className="img-select"
                            classNamePrefix="react-select"
                            options={subcategorylist}
                            placeholder="Choose Sub Category"
                          />
                        </div>
                      </div>
                      <div className="col-lg-2 col-sm-6 col-12">
                        <div className="input-blocks">
                          <StopCircle className="info-img" />
                          <Select
                            className="img-select"
                            classNamePrefix="react-select"
                            options={brandlist}
                            placeholder="Nike"
                          />
                        </div>
                      </div>
                      <div className="col-lg-2 col-sm-6 col-12">
                        <div className="input-blocks">
                          <i className="fas fa-money-bill info-img" />

                          <Select
                            className="img-select"
                            classNamePrefix="react-select"
                            options={price}
                            placeholder="Price"
                          />
                        </div>
                      </div>
                      <div className="col-lg-2 col-sm-6 col-12">
                        <div className="input-blocks">
                          <Link className="btn btn-filters ms-auto">
                            {" "}
                            <i
                              data-feather="search"
                              className="feather-search"
                            />{" "}
                            Search{" "}
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div> */
}
